import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Clear as ClearIcon,
  CameraAlt as CameraIcon,
  QrCodeScanner as QrCodeScannerIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  LocalHospital as HospitalIcon,
} from '@mui/icons-material';
import { materialAPI, categoryAPI, interventionAPI } from '../services/api';
import { isValidGS1Barcode, parseGS1Barcode } from '../utils/gs1Parser';
import { getScannerSettings } from './Admin';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

interface SearchResult {
  id: number;
  name: string;
  article_number: string | null;
  lot_number: string | null;
  expiry_date: string | null;
  current_stock: number;
  cabinet_name: string | null;
  compartment_name: string | null;
  category_name: string | null;
  size: string | null;
  is_consignment: boolean;
}

interface InterventionResult {
  id: number;
  patient_id: string;
  patient_name: string | null;
  started_at: string;
  finished_at: string | null;
  notes: string | null;
  created_by_name: string | null;
  item_count: number;
  matching_items?: Array<{
    material_name: string;
    lot_number: string;
    article_number: string;
    quantity: number;
  }>;
}

const Search: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [interventionResults, setInterventionResults] = useState<InterventionResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<any[]>([]);
  const [cameraEnabled, setCameraEnabled] = useState(false);

  // Suchfelder
  const [lotSearch, setLotSearch] = useState('');
  const [expiryMonths, setExpiryMonths] = useState<number>(3);
  const [freeTextSearch, setFreeTextSearch] = useState('');
  const [categorySearch, setCategorySearch] = useState<number | ''>('');

  useEffect(() => {
    const settings = getScannerSettings();
    setCameraEnabled(settings.cameraEnabled);
    loadCategories();
  }, []);

  useEffect(() => {
    const state = location.state as {
      fromScanner?: boolean;
      scannedCode?: string;
      scanMode?: 'gs1' | 'qr';
    } | null;

    if (state?.fromScanner && state?.scannedCode && state.scanMode === 'gs1') {
      const scannedCode = state.scannedCode;

      setTabValue(0);
      setFreeTextSearch(scannedCode);
      setTimeout(() => {
        void runFreeTextSearch(scannedCode);
      }, 100);

      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const loadCategories = async () => {
    try {
      const response = await categoryAPI.getAll();
      setCategories(response.data || []);
    } catch (err) {
      console.error('Fehler beim Laden der Kategorien:', err);
    }
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setResults([]);
    setInterventionResults([]);
    setError(null);
  };

  const buildFreeTextSearchParams = (input: string) => {
    const trimmedInput = input.trim();

    if (isValidGS1Barcode(trimmedInput)) {
      const parsedGs1 = parseGS1Barcode(trimmedInput);

      if (parsedGs1.gtin) {
        return {
          query: parsedGs1.gtin,
          lot_number: parsedGs1.batchNumber,
        };
      }
    }

    return { query: trimmedInput };
  };

  const openScanner = () => {
    navigate('/scanner', {
      state: {
        returnToSearch: true,
        scanMode: 'gs1',
        autoOpenCamera: true,
      },
    });
  };

  // Chargen-Suche (LOT) - sucht in Materialien UND Interventionsprotokollen
  const handleLotSearch = async () => {
    if (!lotSearch.trim()) {
      setError('Bitte geben Sie eine Chargennummer ein');
      return;
    }
    setLoading(true);
    setError(null);
    setInterventionResults([]);
    
    try {
      // Parallel: Materialien und Interventionsprotokolle durchsuchen
      const [materialResponse, interventionResponse] = await Promise.all([
        materialAPI.search({ lot_number: lotSearch.trim() }),
        interventionAPI.getAll({ lot_number: lotSearch.trim(), limit: 100 })
      ]);
      
      const materials = materialResponse.data || [];
      const interventions = interventionResponse.data?.protocols || [];
      
      setResults(materials);
      setInterventionResults(interventions);
      
      if (materials.length === 0 && interventions.length === 0) {
        setError('Keine Materialien oder Patientenprotokolle mit dieser Chargennummer gefunden');
      }
    } catch (err) {
      console.error('Suchfehler:', err);
      setError('Fehler bei der Suche');
    } finally {
      setLoading(false);
    }
  };

  // Verfallsdatum-Suche
  const handleExpirySearch = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await materialAPI.search({ expiry_months: expiryMonths });
      setResults(response.data || []);
      if ((response.data || []).length === 0) {
        setError(`Keine Materialien mit Verfall in den nächsten ${expiryMonths} Monaten gefunden`);
      }
    } catch (err) {
      console.error('Suchfehler:', err);
      setError('Fehler bei der Suche');
    } finally {
      setLoading(false);
    }
  };

  const runFreeTextSearch = async (searchInput: string) => {
    const searchValue = searchInput.trim();

    if (!searchValue) {
      setError('Bitte geben Sie einen Suchbegriff ein');
      return;
    }
    setLoading(true);
    setError(null);
    setInterventionResults([]);
    try {
      const response = await materialAPI.search(buildFreeTextSearchParams(searchValue));
      setResults(response.data || []);
      if ((response.data || []).length === 0) {
        setError('Keine Materialien gefunden');
      }
    } catch (err) {
      console.error('Suchfehler:', err);
      setError('Fehler bei der Suche');
    } finally {
      setLoading(false);
    }
  };

  // Freitext-Suche
  const handleFreeTextSearch = async () => {
    await runFreeTextSearch(freeTextSearch);
  };

  // Kategorie-Suche
  const handleCategorySearch = async () => {
    if (!categorySearch) {
      setError('Bitte wählen Sie eine Kategorie');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await materialAPI.search({ category_id: categorySearch });
      setResults(response.data || []);
      if ((response.data || []).length === 0) {
        setError('Keine Materialien in dieser Kategorie gefunden');
      }
    } catch (err) {
      console.error('Suchfehler:', err);
      setError('Fehler bei der Suche');
    } finally {
      setLoading(false);
    }
  };

  // Konsignationsware-Suche
  const handleConsignmentSearch = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await materialAPI.search({ is_consignment: true });
      setResults(response.data || []);
      if ((response.data || []).length === 0) {
        setError('Keine Konsignationsmaterialien gefunden');
      }
    } catch (err) {
      console.error('Suchfehler:', err);
      setError('Fehler bei der Suche');
    } finally {
      setLoading(false);
    }
  };

  // Verfallsdatum-Status
  const getExpiryStatus = (expiryDate: string | null) => {
    if (!expiryDate) return { status: 'none', label: 'Kein Datum', color: 'default' as const };
    
    const expiry = new Date(expiryDate);
    const today = new Date();
    const diffDays = Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return { status: 'expired', label: 'Abgelaufen', color: 'error' as const, icon: <ErrorIcon fontSize="small" /> };
    } else if (diffDays <= 30) {
      return { status: 'critical', label: `${diffDays} Tage`, color: 'error' as const, icon: <WarningIcon fontSize="small" /> };
    } else if (diffDays <= 90) {
      return { status: 'warning', label: `${diffDays} Tage`, color: 'warning' as const, icon: <WarningIcon fontSize="small" /> };
    }
    return { status: 'ok', label: `${diffDays} Tage`, color: 'success' as const, icon: <CheckCircleIcon fontSize="small" /> };
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE');
  };

  const clearResults = () => {
    setResults([]);
    setError(null);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Material-Suche
      </Typography>

      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="🔍 Freitext" />
          <Tab label="📦 Chargen (LOT)" />
          <Tab label="⏰ Verfallsdatum" />
          <Tab label="📁 Kategorie" />
          <Tab label="🔴 Konsignation" />
        </Tabs>
      </Paper>

      {/* Freitext-Suche */}
      <TabPanel value={tabValue} index={0}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Freitext-Suche
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Suchen Sie nach Materialname, Artikelnummer, Beschreibung, Notizen oder scannen Sie einen GS1-Barcode.
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  label="Suchbegriff"
                  value={freeTextSearch}
                  onChange={(e) => setFreeTextSearch(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleFreeTextSearch()}
                  placeholder="z.B. Katheter, REF12345 oder GS1-Barcode..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <QrCodeScannerIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        {cameraEnabled && (
                          <Tooltip title="Kamera-Scanner öffnen">
                            <IconButton size="small" onClick={openScanner} color="primary">
                              <CameraIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {freeTextSearch && (
                          <IconButton size="small" onClick={() => setFreeTextSearch('')}>
                            <ClearIcon />
                          </IconButton>
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleFreeTextSearch}
                  disabled={loading}
                  startIcon={<SearchIcon />}
                >
                  Suchen
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Chargen-Suche */}
      <TabPanel value={tabValue} index={1}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Chargen-Suche (LOT) für Rückrufe
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Geben Sie die Chargennummer (LOT) ein, um alle betroffenen Materialien <strong>und Patienten</strong> zu finden.
              Die Suche durchsucht automatisch auch alle Interventionsprotokolle nach verwendeten Chargen.
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  label="Chargennummer (LOT)"
                  value={lotSearch}
                  onChange={(e) => setLotSearch(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleLotSearch()}
                  placeholder="z.B. LOT2024-001, 23456A..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: lotSearch && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setLotSearch('')}>
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleLotSearch}
                  disabled={loading}
                  startIcon={<SearchIcon />}
                  color="warning"
                >
                  Rückruf-Suche
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Interventionsprotokolle Ergebnisse (Patienten) */}
        {interventionResults.length > 0 && (
          <Card sx={{ mt: 3, border: '2px solid', borderColor: 'error.main' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <PersonIcon color="error" />
                <Typography variant="h6" color="error">
                  ⚠️ Betroffene Patienten ({interventionResults.length})
                </Typography>
              </Box>
              <Alert severity="error" sx={{ mb: 2 }}>
                <strong>Achtung:</strong> Diese Patienten haben Material mit der Chargennummer "{lotSearch}" erhalten!
              </Alert>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'error.light' }}>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Patienten-ID</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Patient</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Datum</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Durchgeführt von</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Material</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Aktionen</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {interventionResults.map((intervention) => (
                      <TableRow 
                        key={intervention.id}
                        sx={{ '&:hover': { bgcolor: 'error.50' } }}
                      >
                        <TableCell>
                          <Chip 
                            label={intervention.patient_id} 
                            size="small" 
                            color="error"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          {intervention.patient_name || '-'}
                        </TableCell>
                        <TableCell>
                          {new Date(intervention.started_at).toLocaleDateString('de-DE', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </TableCell>
                        <TableCell>
                          {intervention.created_by_name || '-'}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={`${intervention.item_count} Material(ien)`}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Details anzeigen">
                            <IconButton 
                              size="small"
                              onClick={() => navigate(`/protocols/${intervention.id}`)}
                              color="primary"
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}
      </TabPanel>

      {/* Verfallsdatum-Suche */}
      <TabPanel value={tabValue} index={2}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Verfallsdatum-Suche
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Finden Sie Materialien, die in den nächsten X Monaten ablaufen.
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Zeitraum</InputLabel>
                  <Select
                    value={expiryMonths}
                    onChange={(e) => setExpiryMonths(e.target.value as number)}
                    label="Zeitraum"
                  >
                    <MenuItem value={1}>1 Monat</MenuItem>
                    <MenuItem value={2}>2 Monate</MenuItem>
                    <MenuItem value={3}>3 Monate</MenuItem>
                    <MenuItem value={6}>6 Monate</MenuItem>
                    <MenuItem value={12}>12 Monate</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleExpirySearch}
                  disabled={loading}
                  startIcon={<WarningIcon />}
                  color="error"
                >
                  Ablaufende suchen
                </Button>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Alert severity="info" sx={{ py: 0.5 }}>
                  Zeigt auch bereits abgelaufene Materialien
                </Alert>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Kategorie-Suche */}
      <TabPanel value={tabValue} index={3}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Kategorie-Suche
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Zeigen Sie alle Materialien einer bestimmten Kategorie an.
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={8}>
                <FormControl fullWidth>
                  <InputLabel>Kategorie</InputLabel>
                  <Select
                    value={categorySearch}
                    onChange={(e) => setCategorySearch(e.target.value as number | '')}
                    label="Kategorie"
                  >
                    <MenuItem value="">
                      <em>Bitte wählen...</em>
                    </MenuItem>
                    {categories.map((cat) => (
                      <MenuItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleCategorySearch}
                  disabled={loading}
                  startIcon={<SearchIcon />}
                >
                  Kategorie anzeigen
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Konsignationsware-Suche */}
      <TabPanel value={tabValue} index={4}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Konsignationsware
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Zeigen Sie alle Materialien an, die als Konsignationsware markiert sind.
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={8}>
                <Alert severity="info" sx={{ py: 1 }}>
                  Konsignationsware sind Materialien, die vom Lieferanten bereitgestellt werden und erst bei Verwendung bezahlt werden.
                </Alert>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleConsignmentSearch}
                  disabled={loading}
                  startIcon={<SearchIcon />}
                  color="error"
                >
                  Konsignation anzeigen
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Fehler-Anzeige */}
      {error && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Ergebnisse */}
      {results.length > 0 && (
        <Paper sx={{ mt: 3 }}>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Suchergebnisse ({results.length} Treffer)
            </Typography>
            <Button size="small" onClick={clearResults} startIcon={<ClearIcon />}>
              Ergebnisse löschen
            </Button>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Material</TableCell>
                  <TableCell>Artikelnr.</TableCell>
                  <TableCell>Charge (LOT)</TableCell>
                  <TableCell>Verfall</TableCell>
                  <TableCell align="right">Bestand</TableCell>
                  <TableCell>Standort</TableCell>
                  <TableCell>Kategorie</TableCell>
                  <TableCell align="center">Aktion</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((item) => {
                  const expiryStatus = getExpiryStatus(item.expiry_date);
                  return (
                    <TableRow 
                      key={item.id} 
                      hover
                      onClick={() => navigate(`/materials/${item.id}/edit`)}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: item.is_consignment ? 'rgba(211, 47, 47, 0.08)' : 'transparent',
                        borderLeft: item.is_consignment ? '4px solid #d32f2f' : 'none',
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {item.name}
                            </Typography>
                            {item.size && (
                              <Typography variant="caption" color="text.secondary">
                                {item.size}
                              </Typography>
                            )}
                          </Box>
                          {item.is_consignment && (
                            <Chip 
                              label="K" 
                              size="small" 
                              color="error" 
                              sx={{ height: 18, fontSize: '0.7rem', minWidth: 20 }}
                              title="Konsignationsware"
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{item.article_number || '-'}</TableCell>
                      <TableCell>
                        {item.lot_number ? (
                          <Chip 
                            label={item.lot_number} 
                            size="small" 
                            variant="outlined"
                          />
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {item.expiry_date ? (
                          <Tooltip title={expiryStatus.label}>
                            <Chip 
                              label={formatDate(item.expiry_date)}
                              size="small"
                              color={expiryStatus.color}
                              icon={expiryStatus.icon}
                            />
                          </Tooltip>
                        ) : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <Chip 
                          label={item.current_stock}
                          size="small"
                          color={item.current_stock <= 0 ? 'error' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        {item.cabinet_name || '-'}
                        {item.compartment_name && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {item.compartment_name}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {item.category_name || '-'}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Material bearbeiten">
                          <IconButton
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              navigate(`/materials/${item.id}/edit`);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default Search;
